import React from "react";
import { Linkedin, Twitter, Mail } from "lucide-react";

const GOLD = "#C9A84C";
const GOLD_BRIGHT = "#E8C878";
const PURPLE = "#8B5CD6";
const CREAM = "#F0EBE1";
const CREAM_LO = "rgba(240, 235, 225, 0.65)";
const BG_DARK = "#0A080F";
const BG_DARKER = "#07050E";

const COLS = [
  {
    title: "PRODUCT",
    links: ["How It Works", "Integrations", "Why South Africa", "Security", "Recovery Audit"],
  },
  { title: "COMPANY", links: ["About", "Blog", "Careers", "Contact"] },
  { title: "LEGAL", links: ["Privacy Policy", "POPIA Compliance", "Terms of Service"] },
];

export default function Footer() {
  return (
    <>
      <style>{`
        .modern-footer {
          background: linear-gradient(180deg, ${BG_DARK} 0%, ${BG_DARKER} 100%);
          position: relative;
          overflow: hidden;
        }

        .modern-footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            ${GOLD} 20%,
            ${PURPLE} 50%,
            ${GOLD} 80%,
            transparent 100%
          );
          animation: shimmer 3s ease-in-out infinite;
        }

        @keyframes shimmer {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        .footer-brand {
          padding: 80px 24px 60px;
        }

        .footer-content {
          max-width: 1280px;
          margin: 0 auto;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 48px;
        }

        @media (min-width: 768px) {
          .footer-grid {
            grid-template-columns: 1.5fr 1fr;
            gap: 60px;
          }
        }

        @media (min-width: 1024px) {
          .footer-grid {
            grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
            gap: 40px;
          }
        }

        .brand-section {
          display: flex;
          flex-direction: column;
        }

        .brand-logo {
          height: 40px;
          width: auto;
          margin-bottom: 20px;
          filter: drop-shadow(0 2px 8px rgba(201, 168, 76, 0.3));
          transition: all 0.3s ease;
        }

        .brand-logo:hover {
          filter: drop-shadow(0 4px 12px rgba(201, 168, 76, 0.5));
          transform: scale(1.02);
        }

        .brand-tagline {
          font-family: 'Outfit', sans-serif;
          font-size: 14px;
          font-weight: 400;
          line-height: 1.6;
          color: ${CREAM_LO};
          max-width: 260px;
          margin-bottom: 24px;
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-link {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.1), rgba(201, 168, 76, 0.05));
          border: 1px solid rgba(201, 168, 76, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${GOLD};
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          text-decoration: none;
        }

        .social-link:hover {
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.2), rgba(201, 168, 76, 0.1));
          border-color: rgba(201, 168, 76, 0.4);
          color: ${GOLD_BRIGHT};
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(201, 168, 76, 0.3);
        }

        .footer-column {
          display: flex;
          flex-direction: column;
        }

        .column-title {
          font-family: 'Syne', sans-serif;
          font-size: 11px;
          font-weight: 600;
          color: ${GOLD};
          text-transform: uppercase;
          letter-spacing: 0.14em;
          margin-bottom: 20px;
        }

        .footer-links {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .footer-link {
          font-family: 'Outfit', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: ${CREAM_LO};
          text-decoration: none;
          transition: all 0.2s ease;
          position: relative;
        }

        .footer-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1px;
          background: ${GOLD};
          transition: width 0.3s ease;
        }

        .footer-link:hover {
          color: ${CREAM};
        }

        .footer-link:hover::after {
          width: 100%;
        }

        .cta-section {
          display: flex;
          flex-direction: column;
        }

        .cta-title {
          font-family: 'Syne', sans-serif;
          font-size: 11px;
          font-weight: 600;
          color: ${GOLD};
          text-transform: uppercase;
          letter-spacing: 0.14em;
          margin-bottom: 20px;
        }

        .cta-button {
          background: linear-gradient(135deg, ${GOLD}, ${GOLD_BRIGHT});
          border: none;
          border-radius: 12px;
          padding: 12px 24px;
          font-family: 'Syne', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: ${BG_DARKER};
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 4px 16px rgba(201, 168, 76, 0.25);
          text-decoration: none;
          display: inline-block;
          margin-bottom: 16px;
        }

        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(201, 168, 76, 0.35);
          background: linear-gradient(135deg, ${GOLD_BRIGHT}, ${GOLD});
        }

        .progress-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .progress-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: ${GOLD};
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }

        .progress-text {
          font-family: 'Fira Code', monospace;
          font-size: 11px;
          color: ${CREAM_LO};
        }

        .footer-divider {
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(201, 168, 76, 0.2) 50%,
            transparent 100%
          );
          margin: 48px 0;
        }

        .risk-disclosure {
          background: linear-gradient(135deg, rgba(139, 92, 214, 0.1), rgba(139, 92, 214, 0.05));
          backdrop-filter: blur(10px);
          border: 1px solid rgba(139, 92, 214, 0.2);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 32px;
        }

        .risk-text {
          font-family: 'Outfit', sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 1.6;
          color: ${CREAM_LO};
        }

        .footer-bottom {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        @media (min-width: 768px) {
          .footer-bottom {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
        }

        .footer-copyright {
          font-family: 'Fira Code', monospace;
          font-size: 11px;
          color: ${CREAM_LO};
        }

        .footer-contact {
          font-family: 'Fira Code', monospace;
          font-size: 11px;
          color: ${CREAM_LO};
        }
      `}</style>

      <footer className="modern-footer" data-testid="site-footer">
        <div className="footer-brand">
          <div className="footer-content">
            <div className="footer-grid">
              {/* Brand Section */}
              <div className="brand-section">
                <img
                  src="/crown-ledger-logo.svg"
                  alt="Crown Ledger"
                  className="brand-logo"
                />
                <p className="brand-tagline">
                  Payment recovery infrastructure for South Africa's subscription economy.
                </p>
                <div className="social-links">
                  {[Linkedin, Twitter, Mail].map((Icon, i) => (
                    <a
                      key={i}
                      href="#"
                      className="social-link"
                      data-testid={`footer-social-${i}`}
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Navigation Columns */}
              {COLS.map((col) => (
                <div key={col.title} className="footer-column">
                  <div className="column-title">{col.title}</div>
                  <ul className="footer-links">
                    {col.links.map((link) => (
                      <li key={link}>
                        <a href="#" className="footer-link">
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

            </div>

            {/* Divider */}
            <div className="footer-divider" />
          </div>
        </div>
      </footer>
    </>
  );
}
